/**
 * AssessmentWorkspaceShell — assessment-project view of the workspace, at parity
 * with the video workspace: studio chrome, collapsible chat + right panel, an
 * aspect-ratio stage, a bottom filmstrip with live thumbnails, Blueprint/Studio
 * tabs, a Publish button + share modal (with version history), and a live
 * Analytics/comprehension panel. Edits flow through ivy-chat → MCP; this view
 * reads the synced production state and refreshes on SSE.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import {
  MessageSquare, MessagesSquare, X, Maximize2, Minimize2, PanelRight, BarChart3, Monitor, Square, Smartphone,
  RefreshCw, ChevronLeft, ChevronRight, ArrowUpRight,
} from 'lucide-react';
import Logo from '@/components/ui/logo';
import { EmbeddedIvyChat } from '@/components/agent-chat/EmbeddedIvyChat';
import { ScrollableFilmstrip } from '@/components/video-workspace/ScrollableFilmstrip';
import { AssessmentPublishButton } from './AssessmentPublishButton';
import { AssessmentReviewsPanel } from './reviews/AssessmentReviewsPanel';
import { useAssessmentReviews } from './reviews/useAssessmentReviews';
import { VersionSwitcher } from '@/components/slides/VersionSwitcher';
import {
  assessmentAPI, type AssessmentStudioData, type AssessmentStudioQuestion,
  type AssessmentAnalytics,
} from '@/services/api';
import { conversationUpdatesService } from '@/services/conversationUpdatesService';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { useOutlineEditQueueStore, type OutlineEditItem } from '@/stores/outlineEditQueueStore';
import { Commentable, SectionLabel } from '@/components/outline/Commentable';
import { cn } from '@/lib/utils';

const FORMAT_LABEL: Record<string, string> = {
  experience: 'Interactive', mcq: 'Multiple choice', 'multi-select': 'Multi-select',
  numeric: 'Numeric', ordering: 'Ordering', match: 'Match',
};
const ASPECTS = [
  { id: '16:9', w: 16, h: 9, Icon: Monitor },
  { id: '1:1', w: 1, h: 1, Icon: Square },
  { id: '9:16', w: 9, h: 16, Icon: Smartphone },
] as const;

function useStageFit(ref: React.RefObject<HTMLDivElement | null>, arW: number, arH: number) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const elem = ref.current; if (!elem) return;
    const compute = () => {
      const cw = elem.clientWidth, ch = elem.clientHeight; if (!cw || !ch) return;
      const ar = arW / arH; let w = cw, h = cw / ar;
      if (h > ch) { h = ch; w = ch * ar; }
      setSize({ width: Math.floor(w), height: Math.floor(h) });
    };
    compute(); const ro = new ResizeObserver(compute); ro.observe(elem);
    return () => ro.disconnect();
  }, [ref, arW, arH]);
  return size;
}

// ── options/native rendering ───────────────────────────────────────────────
function normalizeOptions(q: AssessmentStudioQuestion): { key: string; text: string; correct: boolean }[] {
  const opts = Array.isArray(q.options) ? q.options : [];
  const answer = q.answer;
  const answerIdx = answer && typeof answer === 'object' && typeof answer.correct === 'number' ? answer.correct : undefined;
  const answerKey = typeof answer === 'string' ? answer : undefined;
  const correctSet: any[] = Array.isArray(q.correct) ? q.correct : [];
  return opts.map((opt: any, i: number) => {
    const isObj = opt && typeof opt === 'object';
    const key = isObj ? (opt.key ?? String.fromCharCode(65 + i)) : String.fromCharCode(65 + i);
    const text = isObj ? String(opt.text ?? opt.label ?? '') : String(opt);
    const correct = (isObj && opt.correct === true) || (answerKey != null && answerKey === key)
      || (answerIdx != null && answerIdx === i) || correctSet.includes(key) || correctSet.includes(i);
    return { key, text, correct };
  });
}

// Studio preview host. Experiences that register a runner-owned Submit (IvyXP.bind /
// onSubmit) post a 'capable' message and draw NO button of their own — so the studio
// must provide the Submit chrome, mirroring assessment-runner.html. Legacy own-button
// experiences never post 'capable', so no studio bar shows and their in-page control
// drives submission as before.
function ExperienceStage({ html, width, height }: { html: string; width: number; height: number }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [capable, setCapable] = useState(false);
  const [result, setResult] = useState<{ correct: boolean; response: string } | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  useEffect(() => {
    function onMsg(e: MessageEvent) {
      const d = e.data as { __ivy_assessment?: boolean; type?: string; submit?: boolean; isCorrect?: boolean; response?: unknown } | null;
      if (!d || d.__ivy_assessment !== true) return;
      if (ref.current && e.source !== ref.current.contentWindow) return;
      if (d.type === 'capable' && d.submit) setCapable(true);
      else if (d.type === 'answer') setResult({ correct: !!d.isCorrect, response: String(d.response ?? '') });
      else if (d.type === 'needsAnswer') { setHint('Answer in the question first.'); window.setTimeout(() => setHint(null), 2200); }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);
  const submit = () => { setResult(null); ref.current?.contentWindow?.postMessage({ __ivy_xp_grade: true }, '*'); };
  const BAR = capable ? 52 : 0;
  return (
    <div style={{ width, height }} className="studio-slide-shadow rounded-[14px] overflow-hidden flex flex-col">
      <iframe ref={ref} sandbox="allow-scripts" srcDoc={html} title="question"
        style={{ width: '100%', height: height - BAR, border: 0, background: '#faf8f3', display: 'block' }} />
      {capable && (
        <div className="flex items-center gap-3 px-4 bg-white border-t border-black/10" style={{ height: BAR }}>
          {result ? (
            <span className={cn('text-sm font-medium', result.correct ? 'text-emerald-600' : 'text-rose-600')}>
              {result.correct ? '✓ Correct' : '✗ Not quite'}{result.response ? ` — submitted ${result.response}` : ''}
            </span>
          ) : hint ? <span className="text-sm text-amber-600">{hint}</span>
            : <span className="text-xs text-zinc-500">Preview — answer in the question, then Submit</span>}
          <button onClick={submit} className="ml-auto px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
            {result ? 'Re-check' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}
function NativeStage({ q, width, height }: { q: AssessmentStudioQuestion; width: number; height: number }) {
  const options = normalizeOptions(q);
  const numericAnswer = q.answer && typeof q.answer === 'object' && 'value' in q.answer ? q.answer : null;
  return (
    <div style={{ width, height }} className="studio-slide-shadow rounded-[14px] bg-card overflow-y-auto p-7 flex flex-col">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-primary/70 mb-3">{FORMAT_LABEL[q.format] || q.format}</div>
      <p className="text-lg font-medium text-foreground mb-5 leading-snug">{q.stem}</p>
      {options.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {options.map((opt) => (
            <li key={opt.key} className={cn('rounded-xl border px-4 py-3 text-sm flex items-center gap-3',
              opt.correct ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border')}>
              <span className="font-mono text-xs opacity-60">{opt.key}</span><span>{opt.text}</span>
              {opt.correct && <span className="ml-auto text-xs font-semibold whitespace-nowrap">✓ correct</span>}
            </li>
          ))}
        </ul>
      )}
      {numericAnswer && <div className="text-sm text-muted-foreground mt-3">Answer: <span className="font-semibold text-foreground">{String(numericAnswer.value)}{numericAnswer.unit ? ' ' + numericAnswer.unit : ''}</span>{numericAnswer.tolerance != null && <span className="opacity-60"> (±{String(numericAnswer.tolerance)})</span>}</div>}
      {q.explanation && <p className="text-sm text-muted-foreground mt-auto pt-5 border-t border-border">{q.explanation}</p>}
    </div>
  );
}

// Live scaled thumbnail of a question (the real rendered experience / native card).
function FilmstripThumb({ q }: { q: AssessmentStudioQuestion }) {
  if (q.format === 'experience' && q.html) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none bg-[#faf8f3]">
        <iframe sandbox="allow-scripts" srcDoc={q.html} title="" tabIndex={-1}
          style={{ width: 520, height: 360, border: 0, transform: 'scale(0.215)', transformOrigin: 'top left' }} />
      </div>
    );
  }
  return (
    <div className="absolute inset-0 p-2 bg-card overflow-hidden">
      <p className="text-[8px] leading-tight text-foreground/70 line-clamp-4">{q.stem || q.concept || q.id}</p>
    </div>
  );
}

function GradingInspector({ q }: { q: AssessmentStudioQuestion }) {
  const numericAnswer = q.answer && typeof q.answer === 'object' && 'value' in q.answer ? q.answer : null;
  const mcqAnswer = typeof q.answer === 'string' ? q.answer : undefined;
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (v == null || v === '' ? null :
    <div className="flex justify-between gap-3 py-2 text-sm border-b border-border/50 last:border-0"><span className="text-muted-foreground shrink-0">{k}</span><span className="text-foreground text-right font-medium break-words">{v}</span></div>);
  return (
    <div className="p-4 space-y-5 overflow-y-auto h-full">
      <section><h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Classification</h3>
        <div className="rounded-xl border border-border bg-card px-3"><Row k="Format" v={FORMAT_LABEL[q.format] || q.format} /><Row k="Concept" v={q.concept} /><Row k="Archetype" v={q.archetype} /><Row k="Difficulty" v={q.difficulty} /></div></section>
      {Array.isArray(q.conceptTags) && q.conceptTags.length > 0 && (
        <section><h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Concept tags</h3>
          <div className="flex flex-wrap gap-1.5">{q.conceptTags.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-mono">{t}</span>)}</div></section>)}
      <section><h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Grading</h3>
        <div className="rounded-xl border border-border bg-card px-3">
          {numericAnswer && <Row k="Answer" v={`${String(numericAnswer.value)}${numericAnswer.unit ? ' ' + numericAnswer.unit : ''}`} />}
          {numericAnswer?.tolerance != null && <Row k="Tolerance" v={`±${String(numericAnswer.tolerance)}`} />}
          {mcqAnswer && <Row k="Correct key" v={mcqAnswer} />}
          {q.verify && <Row k="Verify ✓" v={JSON.stringify(q.verify.correct)} />}
          {q.verify && <Row k="Verify ✗" v={JSON.stringify(q.verify.wrong)} />}
        </div></section>
      {q.explanation && <section><h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Explanation</h3><p className="text-sm text-foreground/80 leading-relaxed">{q.explanation}</p></section>}
    </div>
  );
}

function Bar({ pct, n }: { pct: number; n?: number }) {
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-500';
  return <div className="flex items-center gap-2"><div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className={cn('h-full rounded-full', color)} style={{ width: pct + '%' }} /></div><span className="text-xs tabular-nums w-16 text-right text-muted-foreground">{pct}%{n != null ? ` ·${n}` : ''}</span></div>;
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function AnalyticsPanel({ conversationId }: { conversationId: string }) {
  const [a, setA] = useState<AssessmentAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); assessmentAPI.analytics(conversationId).then(setA).catch(() => {}).finally(() => setLoading(false)); }, [conversationId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading analytics…</div>;
  if (!a?.published) return <div className="p-6 text-center text-sm text-muted-foreground">Publish the assessment to start collecting comprehension data.</div>;
  const t = a.totals!;
  return (
    <div className="p-4 space-y-5 overflow-y-auto h-full">
      <div className="flex items-center justify-between"><h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Live comprehension</h3>
        <button onClick={load} className="text-muted-foreground hover:text-foreground"><RefreshCw className="w-3.5 h-3.5" /></button></div>
      <div className="grid grid-cols-3 gap-2">
        {[['Learners', t.learners], ['Answers', t.statements], ['Correct', t.graded ? Math.round((t.correct / t.graded) * 100) + '%' : '—']].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-2.5 text-center"><div className="text-lg font-bold text-foreground">{v}</div><div className="text-[10px] text-muted-foreground">{k}</div></div>))}
      </div>
      {a.timing && a.timing.completions > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {[['Avg engaged', fmtMs(a.timing.avgActiveMs)], ['Avg idle', fmtMs(a.timing.avgIdleMs)]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card p-2.5 text-center"><div className="text-base font-bold text-foreground">{v}</div><div className="text-[10px] text-muted-foreground">{k}</div></div>))}
        </div>
      )}
      <section><h4 className="text-xs font-semibold text-foreground mb-2">Mastery by concept</h4>
        <div className="space-y-2">{(a.concepts || []).map((c) => <div key={c.tag}><div className="text-[11px] text-foreground/80 mb-0.5 font-mono truncate">{c.tag.split(':').pop()}</div><Bar pct={c.pct} n={c.total} /></div>)}</div></section>
      <section><h4 className="text-xs font-semibold text-foreground mb-2">By question</h4>
        <div className="space-y-2">{(a.perQuestion || []).map((q) => <div key={q.id}><div className="text-[11px] text-foreground/80 mb-0.5 flex justify-between gap-2"><span>{q.id} · {q.type}</span>{q.timing?.avgTimeToAnswerMs != null && <span className="text-muted-foreground tabular-nums shrink-0">{fmtMs(q.timing.avgTimeToAnswerMs)}{q.timing.avgVisits != null && q.timing.avgVisits > 1.05 ? ` · ${q.timing.avgVisits.toFixed(1)}×` : ''}</span>}</div><Bar pct={q.pct} n={q.total} /></div>)}</div></section>
    </div>
  );
}

// Per-question version history (the SlideVersion analogue) for the selected
// experience question. Reuses the slide VersionSwitcher dropdown.
function QuestionVersionSwitcher({ conversationId, qId, onRestored }: { conversationId: string; qId: string; onRestored: () => void }) {
  const [versions, setVersions] = useState<any[]>([]);
  const [current, setCurrent] = useState<number | null>(null);
  const [switching, setSwitching] = useState(false);
  const load = useCallback(() => {
    assessmentAPI.listQuestionVersions(conversationId, qId)
      .then((r) => { setVersions(r.versions || []); setCurrent(r.currentVersion); })
      .catch(() => { setVersions([]); setCurrent(null); });
  }, [conversationId, qId]);
  useEffect(() => { load(); }, [load]);
  if (versions.length < 2) return null; // nothing to switch between yet
  const onSwitch = async (v: number) => {
    setSwitching(true);
    try { const r = await assessmentAPI.activateQuestionVersion(conversationId, qId, v); if (r.success) { setCurrent(v); onRestored(); setTimeout(load, 600); } }
    finally { setSwitching(false); }
  };
  return <VersionSwitcher versions={versions} currentVersion={current} onSwitch={onSwitch} isSwitching={switching} className="h-7 text-xs" />;
}

export const AssessmentWorkspaceShell: React.FC<{ conversationId: string; conversationTitle?: string; autoSendMessage?: string }> = ({ conversationId, conversationTitle, autoSendMessage }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<AssessmentStudioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const [tab, setTab] = useState<'studio' | 'blueprint'>('studio');
  const [showChat, setShowChat] = useState(true);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [rightPanel, setRightPanel] = useState<'details' | 'analytics' | 'reviews' | null>('details');
  const [aspect, setAspect] = useState<typeof ASPECTS[number]>(ASPECTS[0]);
  const [published, setPublished] = useState(false);

  // Lightweight reviews read for the toolbar unread badge (only polls once published).
  const { reviews: allReviews } = useAssessmentReviews(conversationId, published);
  const openReviewCount = useMemo(
    () => allReviews.reduce((n, r) => n + r.comments.filter((c) => c.status === 'pending').length, 0),
    [allReviews],
  );

  const stageRef = useRef<HTMLDivElement>(null);
  const stage = useStageFit(stageRef, aspect.w, aspect.h);

  const refetch = useCallback(async () => {
    try { const d = await assessmentAPI.get(conversationId); setData(d); setSelected((s) => Math.min(s, Math.max(0, (d.questions?.length || 1) - 1))); }
    catch (e) { console.error('[AssessmentStudio] load failed', e); } finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => { setLoading(true); refetch(); }, [refetch]);
  useEffect(() => {
    const onUpdate = (e: Event) => { if ((e as CustomEvent).detail?.conversationId === conversationId) refetch(); };
    window.addEventListener('outline-updated', onUpdate);
    window.addEventListener('assessment-updated', onUpdate);
    const unsub = conversationUpdatesService.onConversationUpdate((id) => { if (id === conversationId) refetch(); });
    return () => { window.removeEventListener('outline-updated', onUpdate); window.removeEventListener('assessment-updated', onUpdate); unsub(); };
  }, [conversationId, refetch]);

  // Chat-driven edits (agent runs safe_edit_file + sync_workspace) don't emit a
  // dedicated SSE event the studio can key on. So when a chat TURN finishes
  // (isStreaming true→false), refetch the synced production state. A short delay
  // lets the agent's final sync_workspace land first.
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    const unsub = useEmbeddedIvyChatStore.subscribe((s) => {
      const streaming = s.isStreaming;
      if (wasStreamingRef.current && !streaming) {
        setTimeout(() => refetch(), 1200);
      }
      wasStreamingRef.current = streaming;
    });
    return () => unsub();
  }, [refetch]);

  const questions = data?.questions || [];
  const q = questions[selected];

  const systemContext = useMemo(() => {
    const parts = [`[project_id: ${conversationId}]`, '[project_kind: assessment]', '[view: assessment_studio]'];
    if (data?.title) parts.push(`[project: "${data.title}"]`);
    if (data?.meta?.subject || data?.meta?.grade) parts.push(`[subject_grade: ${[data?.meta?.subject, data?.meta?.grade].filter(Boolean).join(' · ')}]`);
    if (data?.designSystemId) parts.push(`[design-system: ${data.designSystemId}]`);
    parts.push(`[questions: ${questions.length}]`);
    if (q) parts.push(`[active_question: ${q.id} format=${q.format}${q.concept ? ` concept=${q.concept}` : ''}]`);
    parts.push('[note: This is an interactive ASSESSMENT project, NOT a video. Its questions live in plan.json in THIS project workspace (project_id above). To describe or edit it, read plan.json via safe_read_file, and use the assessment tools (validate_assessment_plan, prepare_question_workspaces, harvest_assessment_manifest, sync_workspace, publish_assessment) and the question-builder / assessment-planner agents, or the /create-assessment skill. After any edit, run sync_workspace + harvest_assessment_manifest so this studio refreshes. Do NOT search other projects.]');
    return parts.join(' ');
  }, [conversationId, data, questions.length, q]);

  const chatWidth = showChat ? (chatExpanded ? '46%' : 360) : 0;
  const goPrev = () => setSelected((s) => Math.max(0, s - 1));
  const goNext = () => setSelected((s) => Math.min(questions.length - 1, s + 1));

  return (
    <div className="h-full w-full flex flex-col studio-elegant-bg text-foreground overflow-hidden">
      {/* Top bar */}
      <div className="h-11 flex-shrink-0 flex items-center justify-between px-3 studio-topbar z-50 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate({ to: '/', search: { slide: undefined } as any })} title="Back to projects" className="flex items-center hover:opacity-80 transition-opacity pr-1"><Logo variant="compact" className="h-7" /></button>
          {!showChat && <button onClick={() => setShowChat(true)} className="h-8 px-2.5 gap-1.5 flex items-center rounded-md studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5"><MessageSquare className="w-4 h-4" /><span className="text-xs">Chat</span></button>}
          <div className="text-sm font-semibold truncate max-w-[220px]">{data?.title || conversationTitle || 'Assessment'}</div>
          <span className="text-[10px] studio-text-secondary px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/5">Assessment</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-black/10 dark:bg-white/5">
            {(['studio', 'blueprint'] as const).map((t) => <button key={t} onClick={() => setTab(t)} className={cn('h-7 px-3 rounded-md text-xs capitalize transition-colors', tab === t ? 'bg-card studio-text-primary shadow-sm' : 'studio-text-secondary hover:studio-text-primary')}>{t}</button>)}
          </div>
          {tab === 'studio' && <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-black/10 dark:bg-white/5">
            {ASPECTS.map(({ id, Icon }) => <button key={id} onClick={() => setAspect(ASPECTS.find(a => a.id === id)!)} title={id} className={cn('h-7 px-2 rounded-md flex items-center gap-1 text-[11px] transition-colors', aspect.id === id ? 'bg-card studio-text-primary shadow-sm' : 'studio-text-secondary hover:studio-text-primary')}><Icon className="w-3.5 h-3.5" /></button>)}
          </div>}
          {tab === 'studio' && q?.format === 'experience' && <QuestionVersionSwitcher conversationId={conversationId} qId={q.id} onRestored={refetch} />}
        </div>
        <div className="flex items-center gap-1">
          {published && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 font-medium">● Live</span>}
          <AssessmentPublishButton conversationId={conversationId} onPublishedChange={setPublished} />
          <button onClick={() => setRightPanel(p => p === 'reviews' ? null : 'reviews')} title="Reviews" className={cn('relative w-8 h-8 flex items-center justify-center rounded-md studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5', rightPanel === 'reviews' && 'bg-black/5 dark:bg-white/5 studio-text-primary')}>
            <MessagesSquare className="w-4 h-4" />
            {openReviewCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-amber-500 text-amber-950 text-[9px] font-semibold flex items-center justify-center tabular-nums">{openReviewCount}</span>}
          </button>
          <button onClick={() => setRightPanel(p => p === 'analytics' ? null : 'analytics')} title="Analytics" className={cn('w-8 h-8 flex items-center justify-center rounded-md studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5', rightPanel === 'analytics' && 'bg-black/5 dark:bg-white/5 studio-text-primary')}><BarChart3 className="w-4 h-4" /></button>
          <button onClick={() => setRightPanel(p => p === 'details' ? null : 'details')} title="Details" className={cn('w-8 h-8 flex items-center justify-center rounded-md studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5', rightPanel === 'details' && 'bg-black/5 dark:bg-white/5 studio-text-primary')}><PanelRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Chat */}
        <motion.div className="flex-shrink-0 flex flex-col studio-panel-elevated overflow-hidden" initial={false} animate={{ width: chatWidth, opacity: showChat ? 1 : 0 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }}>
          <div className="h-10 flex items-center justify-between px-3 flex-shrink-0"><span className="text-xs font-semibold studio-text-primary">Chat</span>
            <div className="flex items-center gap-1"><button onClick={() => setChatExpanded(e => !e)} className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5">{chatExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}</button>
              <button onClick={() => { setShowChat(false); setChatExpanded(false); }} className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5"><X className="w-4 h-4" /></button></div></div>
          <div className="flex-1 overflow-hidden"><EmbeddedIvyChat conversationId={conversationId} conversationTitle={data?.title || conversationTitle} systemContext={systemContext} autoSendMessage={autoSendMessage} /></div>
        </motion.div>

        {/* Center */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          {tab === 'blueprint' ? (
            <BlueprintView data={data} loading={loading} conversationId={conversationId} onPick={(i) => { setSelected(i); setTab('studio'); }} />
          ) : (
            <>
              <div ref={stageRef} className="flex-1 min-h-0 studio-canvas flex items-center justify-center p-5 overflow-hidden relative">
                {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
                  : questions.length === 0 ? <div className="max-w-sm text-center text-sm text-muted-foreground">No questions yet. Ask in chat to build them.</div>
                  : !q ? null : q.format === 'experience'
                    ? (q.html ? <ExperienceStage key={`${q.id}:${q.html.length}`} html={q.html} width={stage.width} height={stage.height} /> : <div style={{ width: stage.width, height: stage.height }} className="rounded-[14px] border border-dashed border-border flex items-center justify-center text-sm text-muted-foreground">Not synced yet.</div>)
                    : <NativeStage q={q} width={stage.width} height={stage.height} />}
                {questions.length > 1 && <>
                  <button onClick={goPrev} disabled={selected === 0} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-card border border-border shadow flex items-center justify-center disabled:opacity-30 hover:bg-accent"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={goNext} disabled={selected === questions.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-card border border-border shadow flex items-center justify-center disabled:opacity-30 hover:bg-accent"><ChevronRight className="w-4 h-4" /></button>
                </>}
              </div>
              {questions.length > 0 && (
                <div className="flex-shrink-0 border-t border-black/5 dark:border-white/5 py-2.5 flex items-center gap-2">
                  <span className="text-[11px] studio-text-secondary px-3 shrink-0">Q{selected + 1} / {questions.length}</span>
                  <div className="flex-1 min-w-0"><ScrollableFilmstrip height="h-[72px]" gap="gap-2" paddingX="px-2">
                    {questions.map((qq, i) => (
                      <button key={qq.id} onClick={() => setSelected(i)} title={qq.stem}
                        className={cn('group relative w-[112px] h-[64px] rounded-lg overflow-hidden cursor-pointer transition-all border-2 flex-shrink-0', i === selected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-zinc-300 dark:hover:border-zinc-700')}>
                        <FilmstripThumb key={`${qq.id}:${qq.html?.length || 0}`} q={qq} />
                        <span className="absolute top-1 left-1 text-[9px] font-mono bg-black/50 text-white px-1 rounded">Q{i + 1}</span>
                        <span className={cn('absolute top-1 right-1 text-[8px] px-1 py-0.5 rounded-full', qq.format === 'experience' ? 'bg-primary/80 text-white' : 'bg-zinc-700/80 text-white')}>{qq.format === 'experience' ? 'INX' : 'quick'}</span>
                        {qq.missing && <span className="absolute bottom-0.5 right-1 text-[9px] text-amber-400">⚠</span>}
                      </button>
                    ))}
                  </ScrollableFilmstrip></div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right panel */}
        <motion.div className="flex-shrink-0 flex flex-col studio-panel-elevated overflow-hidden border-l border-black/5 dark:border-white/5" initial={false} animate={{ width: rightPanel ? 320 : 0, opacity: rightPanel ? 1 : 0 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }}>
          <div className="h-10 flex items-center justify-between px-3 flex-shrink-0"><span className="text-xs font-semibold studio-text-primary">{rightPanel === 'analytics' ? 'Analytics' : rightPanel === 'reviews' ? 'Reviews' : q ? `Q${selected + 1} details` : 'Details'}</span>
            <button onClick={() => setRightPanel(null)} className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5"><X className="w-4 h-4" /></button></div>
          <div className="flex-1 min-h-0">{rightPanel === 'analytics' ? <AnalyticsPanel conversationId={conversationId} /> : rightPanel === 'reviews' ? <AssessmentReviewsPanel conversationId={conversationId} onJumpToQuestion={(i) => { setSelected(i); setTab('studio'); }} /> : q ? <GradingInspector q={q} /> : null}</div>
        </motion.div>
      </div>
    </div>
  );
};

const DIFF_STYLE: Record<string, string> = {
  easy: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  hard: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

// Live preview that fills its container — experience iframe or a format placeholder.
function BlueprintPreview({ q }: { q: AssessmentStudioQuestion }) {
  if (q.format === 'experience' && q.html) {
    return (
      <div className="absolute inset-0 overflow-hidden bg-[#faf8f3] pointer-events-none">
        <iframe sandbox="allow-scripts" srcDoc={q.html} title="" tabIndex={-1}
          style={{ width: 520, height: 360, border: 0, transform: 'scale(0.37)', transformOrigin: 'top left' }} />
      </div>
    );
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-muted/60 text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-center px-2">
      {FORMAT_LABEL[q.format] || q.format}
    </div>
  );
}

function BlueprintQuestionCard({ q, index, conversationId, onPick }: { q: AssessmentStudioQuestion; index: number; conversationId?: string; onPick: () => void }) {
  const sceneLabel = `Q${index + 1}`;
  const queue = useOutlineEditQueueStore((s) => (conversationId ? s.queuesByConversation[conversationId] : undefined));
  const commentsByAnchor = useMemo(() => {
    const out: Record<string, OutlineEditItem[]> = {};
    for (const it of queue ?? []) {
      if (it.kind === 'comment' && it.slideId === q.id && it.anchorId) (out[it.anchorId] ??= []).push(it);
    }
    return out;
  }, [queue, q.id]);
  const ctx = (anchorId: string, anchorLabel: string) => ({
    anchorId, anchorLabel, sceneLabel, conversationId, slideId: q.id,
    comments: commentsByAnchor[anchorId] ?? [],
  });

  const options = normalizeOptions(q);
  const numericAnswer = q.answer && typeof q.answer === 'object' && 'value' in q.answer ? q.answer : null;
  const mcqAnswer = typeof q.answer === 'string' ? q.answer : undefined;

  return (
    <div className={cn('rounded-xl border bg-card overflow-hidden', q.missing ? 'border-amber-400/60' : 'border-border')}>
      <div className="flex gap-4 p-4">
        {/* Left — live preview */}
        <button onClick={onPick} title="Open in studio" className="group/prev relative shrink-0 rounded-lg overflow-hidden ring-1 ring-black/5 dark:ring-white/10" style={{ width: 192, height: 133 }}>
          <BlueprintPreview q={q} />
          <span className="absolute inset-0 bg-black/0 group-hover/prev:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover/prev:opacity-100">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-white text-[11px] font-medium"><ArrowUpRight className="w-3 h-3" /> Open in studio</span>
          </span>
        </button>

        {/* Right — header + commentable notes */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{sceneLabel}</span>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', q.format === 'experience' ? 'bg-primary/15 text-primary dark:text-accent-foreground' : 'bg-muted text-muted-foreground')}>{FORMAT_LABEL[q.format] || q.format}</span>
            {q.difficulty && <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full', DIFF_STYLE[q.difficulty] || 'bg-muted text-muted-foreground')}>{q.difficulty}</span>}
            {q.concept && <span className="text-[11px] text-muted-foreground font-mono truncate">{q.concept}</span>}
            {q.archetype && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-mono">{q.archetype}</span>}
            {q.missing && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">⚠ not synced</span>}
            <button onClick={onPick} className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"><ArrowUpRight className="w-3 h-3" /> Studio</button>
          </div>

          <Commentable {...ctx('stem', 'Question')}>
            <p className="text-sm text-foreground/90 leading-snug">{q.stem || <span className="italic text-muted-foreground">No stem.</span>}</p>
          </Commentable>

          {options.length > 0 && (
            <Commentable {...ctx('options', 'Options')}>
              <SectionLabel>Options</SectionLabel>
              <ul className="space-y-1">
                {options.map((opt) => (
                  <li key={opt.key} className={cn('flex items-center gap-2 text-[13px] rounded-md px-2 py-1', opt.correct ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'text-foreground/80')}>
                    <span className="font-mono text-[11px] opacity-60">{opt.key}</span><span className="flex-1 min-w-0">{opt.text}</span>
                    {opt.correct && <span className="text-[11px] font-semibold whitespace-nowrap">✓</span>}
                  </li>
                ))}
              </ul>
            </Commentable>
          )}

          {(numericAnswer || mcqAnswer) && (
            <Commentable {...ctx('answer', 'Answer key')}>
              <SectionLabel>Answer key</SectionLabel>
              <p className="text-[13px] text-foreground/90">
                {numericAnswer
                  ? <>{String(numericAnswer.value)}{numericAnswer.unit ? ' ' + numericAnswer.unit : ''}{numericAnswer.tolerance != null && <span className="text-muted-foreground"> (±{String(numericAnswer.tolerance)})</span>}</>
                  : <span className="font-mono">{mcqAnswer}</span>}
              </p>
            </Commentable>
          )}

          {Array.isArray(q.conceptTags) && q.conceptTags.length > 0 && (
            <div className="flex flex-wrap gap-1 pl-1.5 pt-0.5">
              {q.conceptTags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-mono">{t}</span>)}
            </div>
          )}

          {q.explanation && (
            <Commentable {...ctx('explanation', 'Explanation')}>
              <SectionLabel>Explanation</SectionLabel>
              <p className="text-[13px] text-muted-foreground leading-snug">{q.explanation}</p>
            </Commentable>
          )}
        </div>
      </div>
    </div>
  );
}

function BlueprintView({ data, loading, conversationId, onPick }: { data: AssessmentStudioData | null; loading: boolean; conversationId?: string; onPick: (i: number) => void }) {
  if (loading) return <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  const qs = data?.questions || [];
  const byFormat: Record<string, number> = {}; const byDiff: Record<string, number> = {}; const byConcept: Record<string, number> = {};
  qs.forEach(q => { byFormat[q.format] = (byFormat[q.format] || 0) + 1; const d = q.difficulty || 'unspecified'; byDiff[d] = (byDiff[d] || 0) + 1; if (q.concept) byConcept[q.concept] = (byConcept[q.concept] || 0) + 1; });
  const chip = (o: Record<string, number>) => Object.entries(o).map(([k, n]) => <span key={k} className="text-xs px-2 py-1 rounded-lg bg-muted text-muted-foreground">{k}: <b className="text-foreground">{n}</b></span>);
  return (
    <div className="flex-1 overflow-y-auto studio-canvas p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <div><h2 className="text-lg font-semibold">{data?.title || 'Assessment'}</h2><p className="text-sm text-muted-foreground">{data?.meta?.subject} · {data?.meta?.grade} · {qs.length} questions · pass {data?.meta?.passThreshold}%</p></div>
        <div className="flex flex-wrap gap-2"><span className="text-xs font-semibold text-muted-foreground self-center">Format</span>{chip(byFormat)}</div>
        <div className="flex flex-wrap gap-2"><span className="text-xs font-semibold text-muted-foreground self-center">Difficulty</span>{chip(byDiff)}</div>
        <div className="flex flex-wrap gap-2"><span className="text-xs font-semibold text-muted-foreground self-center">Concepts</span>{chip(byConcept)}</div>
        {conversationId && <p className="text-[11px] text-muted-foreground/80">Hover any line and click 💬 to leave a note — your comments queue above the chat and send to Ivy on your next message.</p>}
        <div className="space-y-3">
          {qs.map((q, i) => (
            <BlueprintQuestionCard key={q.id} q={q} index={i} conversationId={conversationId} onPick={() => onPick(i)} />
          ))}
        </div>
      </div>
    </div>
  );
}
